<template>
    <div>
        <button
            @click="addPOKJA"
            class="px-4 py-2 bg-green-500 text-white rounded mb-5"
        >
            Tambah POKJA
        </button>
        <div
            v-for="(pokja, index) in pokjaList"
            :key="pokja.pokjaUniqueId || index"
            class="pokja-entry mb-3"
        >
            <div class="lg:w-full md:w-full">
                <div class="flex items-center gap-2">
                    <Dropdown
                        class="w-full"
                        v-model="pokjaList[index].id"
                        :options="getOptionsForDropdown(index)"
                        optionLabel="fullname"
                        optionValue="id"
                        filter
                        filterBy="fullname"
                        @filter="(event) => fetchPOKJAForDropdown(event, index)"
                        @change="validateUniqueness(index)"
                        placeholder="Pilih POKJA Pengadaan"
                    />
                    <button
                        @click="removePOKJA(index)"
                        class="px-4 py-2 bg-red-500 text-white rounded ml-2"
                    >
                        Hapus
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from "vue";
import Dropdown from "primevue/dropdown";
import axios from "axios";

export default defineComponent({
    components: {
        Dropdown,
    },
    props: {
        list_pokja: Array,
        pokja_id : String,
    },
    data() {
        const pokjaInitialList = this.list_pokja
            ? this.list_pokja.map((item, idx) => ({
                ...item,
                pokjaUniqueId: item.pokjaUniqueId || this.pokjaGenerateUniqueId(),
                pokjaDisplayIndex: idx + 1
            }))
            : [];
        return {
            // Global options cache to avoid redundant API calls
            globalPokjaCache: new Map(),
            // Individual options for each dropdown
            dropdownOptions: new Map(),
            // Selected items cache for quick lookup
            selectedItemsCache: new Map(),
            pokjaList: pokjaInitialList,
            pokjaNextIndex: (pokjaInitialList.length > 0)
                ? Math.max(...pokjaInitialList.map(item => item.pokjaDisplayIndex || 0)) + 1
                : 1,
            // Debounce timers for API calls
            debounceTimers: new Map(),
        };
    },
    watch: {
        list_pokja: {
            handler(pokjaNewValue) {
                if (pokjaNewValue) {
                    // Block recursive updates by checking if the internal change is happening
                    if (this._internalUpdate) return;
                    
                    // Preserve pokjaUniqueIds if present, generate new ones if not
                    this.pokjaList = pokjaNewValue.map((item, idx) => ({
                        ...item,
                        pokjaUniqueId: item.pokjaUniqueId || this.pokjaGenerateUniqueId(),
                        pokjaDisplayIndex: item.pokjaDisplayIndex || (idx + 1)
                    }));
                    
                    // Update pokjaNextIndex based on highest current pokjaDisplayIndex
                    if (this.pokjaList.length > 0) {
                        this.pokjaNextIndex = Math.max(
                            ...this.pokjaList.map(item => item.pokjaDisplayIndex || 0)
                        ) + 1;
                    }
                    
                    // Update selected items cache
                    this.updateSelectedItemsCache();
                } else {
                    this.pokjaList = [];
                    this.pokjaNextIndex = 1;
                    this.selectedItemsCache.clear();
                }
            },
            deep: true,
        },
        pokjaList: {
            handler(pokjaNewValue) {
                // Prevent recursive updates by setting a flag
                this._internalUpdate = true;
                // Emit the updated pokjaList to the parent component
                this.$emit("update:list_pokja", pokjaNewValue);
                // Update selected items cache
                this.updateSelectedItemsCache();
                // Reset flag after this update cycle
                this.$nextTick(() => {
                    this._internalUpdate = false;
                });
            },
            deep: true,
        },
    },
    mounted() {
        this.initializePokjaOptions();
    },
    methods: {
        pokjaGenerateUniqueId(){
            return Date.now() + '-' + Math.floor(Math.random() * 100);
        },
        addPOKJA() {
            const newPokja = {
                id: null,
                pokjaUniqueId : this.pokjaGenerateUniqueId(),
                pokjaDisplayIndex : this.pokjaNextIndex
            };
            this.pokjaList.push(newPokja);
            this.pokjaNextIndex++;
            
            // Initialize options for the new dropdown
            const newIndex = this.pokjaList.length - 1;
            this.initializeDropdownOptions(newIndex);
        },
        removePOKJA(pokjaIndex) {
            // Clean up dropdown-specific data
            const pokjaUniqueId = this.pokjaList[pokjaIndex]?.pokjaUniqueId;
            if (pokjaUniqueId) {
                this.dropdownOptions.delete(pokjaUniqueId);
                this.debounceTimers.delete(pokjaUniqueId);
            }
            
            this.pokjaList.splice(pokjaIndex, 1);
        },
        async initializePokjaOptions() {
            // Load initial options for all existing dropdowns
            await this.fetchPOKJAOptions("");
            
            // Initialize options for existing dropdowns
            this.pokjaList.forEach((_, index) => {
                this.initializeDropdownOptions(index);
            });
        },
        initializeDropdownOptions(dropdownIndex) {
            const pokja = this.pokjaList[dropdownIndex];
            if (!pokja) return;
            
            const pokjaUniqueId = pokja.pokjaUniqueId;
            if (!pokjaUniqueId) return;
            
            // Initialize with cached options
            const cachedOptions = this.globalPokjaCache.get("") || [];
            this.dropdownOptions.set(pokjaUniqueId, [...cachedOptions]);
            
            // Ensure selected item is included
            this.ensureSelectedItemInOptions(dropdownIndex);
        },
        async fetchPOKJAForDropdown(event, dropdownIndex) {
            const pokja = this.pokjaList[dropdownIndex];
            if (!pokja) return;
            
            const pokjaUniqueId = pokja.pokjaUniqueId;
            const keyword = event.value || "";
            
            // Clear existing debounce timer
            if (this.debounceTimers.has(pokjaUniqueId)) {
                clearTimeout(this.debounceTimers.get(pokjaUniqueId));
            }
            
            // Set new debounce timer
            const timer = setTimeout(async () => {
                try {
                    const options = await this.fetchPOKJAOptions(keyword);
                    // Update options for this specific dropdown
                    this.dropdownOptions.set(pokjaUniqueId, options);
                    // Ensure selected item is still available
                    this.ensureSelectedItemInOptions(dropdownIndex);
                } catch (error) {
                    console.error("Error fetching pokja for dropdown:", error);
                }
            }, 300); // 300ms debounce
            
            this.debounceTimers.set(pokjaUniqueId, timer);
        },
        async fetchPOKJAOptions(keyword) {
            // Check cache first
            if (this.globalPokjaCache.has(keyword)) {
                return this.globalPokjaCache.get(keyword);
            }
            
            try {
                const response = await axios.post(
                    "/organisasi_pengadaan/search_pokja",
                    {
                        params: {
                            keyword: keyword,
                        },
                    }
                );
                
                if (response.data && Array.isArray(response.data)) {
                    // Cache the results
                    this.globalPokjaCache.set(keyword, response.data);
                    return response.data;
                }
            } catch (error) {
                console.error("Error fetching pokja:", error);
            }
            
            return [];
        },
        getOptionsForDropdown(dropdownIndex) {
            const pokja = this.pokjaList[dropdownIndex];
            if (!pokja || !pokja.pokjaUniqueId) return [];
            
            const options = this.dropdownOptions.get(pokja.pokjaUniqueId) || [];
            
            // Filter out options that are already selected in other dropdowns
            const filteredOptions = options.filter(option => {
                const isSelectedElsewhere = this.pokjaList.some((item, idx) => 
                    idx !== dropdownIndex && item.id === option.id
                );
                return !isSelectedElsewhere;
            });
            
            // Always include the currently selected item for this dropdown
            const currentlySelected = this.selectedItemsCache.get(pokja.id);
            if (currentlySelected && !filteredOptions.some(opt => opt.id === currentlySelected.id)) {
                filteredOptions.unshift(currentlySelected);
            }
            
            return filteredOptions;
        },
        ensureSelectedItemInOptions(dropdownIndex) {
            const pokja = this.pokjaList[dropdownIndex];
            if (!pokja || !pokja.id || !pokja.pokjaUniqueId) return;
            
            const currentOptions = this.dropdownOptions.get(pokja.pokjaUniqueId) || [];
            const selectedItem = this.selectedItemsCache.get(pokja.id);
            
            // If selected item is not in current options, add it
            if (selectedItem && !currentOptions.some(opt => opt.id === pokja.id)) {
                const updatedOptions = [selectedItem, ...currentOptions];
                this.dropdownOptions.set(pokja.pokjaUniqueId, updatedOptions);
            }
        },
        updateSelectedItemsCache() {
            // Cache selected items for quick lookup
            this.pokjaList.forEach(pokja => {
                if (pokja.id && pokja.fullname) {
                    this.selectedItemsCache.set(pokja.id, {
                        id: pokja.id,
                        fullname: pokja.fullname
                    });
                }
            });
        },
        validateUniqueness(pokjaCurrentIndex) {
            const pokjaCurrentId = this.pokjaList[pokjaCurrentIndex]?.id;
            if (
                pokjaCurrentId &&
                this.pokjaList.filter(
                    (pokja) => pokja.id === pokjaCurrentId
                ).length > 1
            ) {
                this.$toast.add({
                    severity: "error",
                    summary: "Error",
                    detail: "POKJA duplikat terdeteksi. Pastikan setiap pokja unik.",
                    life: 3000,
                });
                this.pokjaList[pokjaCurrentIndex].id = null;
            }
        },
    },
});
</script>
